/** @format */

import { reactive } from "vue";
import {
  cartPolicy,
  readClientState,
  removeClientState,
  writeClientState,
} from "@/utils/clientState";

/**
 * Reactive cart store backed by localStorage — the modern replacement for the
 * old `get-cart`/`save-cart` event-bus channel and the App.vue cart
 * prop-drilling. Import `useCart()` anywhere that needs to read or mutate the
 * cart; every consumer shares the same reactive instance.
 *
 * The persisted cart is a loose runtime shape (not the aspirational `Cart`
 * type), so line items are typed permissively with an index signature.
 */
export interface CartLineItem {
  id: number;
  // Unique per line item. Generated with crypto.randomUUID() at add-time (see
  // generateLineItemId). Historically a `Date.now()` number, which collided on
  // same-millisecond adds — and since removal filters by lineItemId, a collision
  // removed BOTH lines (outstanding-hrz). Typed `string | number` so any cart
  // persisted by an older build (numeric ids) still rehydrates and removes
  // correctly.
  lineItemId: string | number;
  selected: unknown[];
  subtotal: number;
  thumbnail_url?: string;
  image_url?: string;
  [key: string]: unknown;
}

export interface RuntimeCart {
  items: CartLineItem[];
}

// Lifecycle (versioning / legacy migration / item vetting) is owned by the
// cart policy in utils/clientState (outstanding-qbo.10): reads migrate legacy
// pre-envelope carts and drop line items that cannot check out (no numeric
// optionIds — they 400 the address step), writes persist the versioned
// envelope. The key name is unchanged ("cart") so existing browsers keep
// their carts.
const STORAGE_KEY = cartPolicy.key;

/**
 * A process-unique id for a cart line item. Prefers crypto.randomUUID() (no
 * collisions even on same-millisecond adds — the bug behind outstanding-hrz);
 * falls back to a monotonic counter + random suffix where crypto is unavailable
 * (older runtimes / non-secure contexts) so two rapid adds still never collide.
 */
let lineItemCounter = 0;
export function generateLineItemId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  lineItemCounter += 1;
  return `li_${Date.now()}_${lineItemCounter}_${Math.random()
    .toString(36)
    .slice(2)}`;
}

const cart = reactive<RuntimeCart>({ items: [] });

function readFromStorage(): RuntimeCart {
  return readClientState(cartPolicy) ?? { items: [] };
}

// Hydrate the shared store once at module load.
cart.items = readFromStorage().items;

// Cross-tab sync: the browser fires a `storage` event in *other* tabs (never the
// one that made the change) whenever this origin's localStorage is written. A
// purchase/clear in one tab would otherwise leave another tab showing a stale
// cart (audit finding), since the store is a module singleton hydrated only once
// above. Re-hydrate when our key changes (`event.key === STORAGE_KEY`, or `null`
// on a cross-tab clear()). Guarded for SSR/no-window and wrapped in try/catch —
// addEventListener/localStorage can throw in locked-down contexts (private mode).
try {
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event: StorageEvent) => {
      if (event.key === STORAGE_KEY || event.key === null) {
        cart.items = readFromStorage().items;
      }
    });
  }
} catch {
  // ignore — cross-tab sync is best-effort; listener registration must never throw
}

export function useCart() {
  /** Persist a cart (versioned envelope) and mirror it into the reactive store. */
  function saveCart(next: RuntimeCart): void {
    const items = Array.isArray(next.items) ? next.items : [];
    // writeClientState never throws (quota / private mode) — saving is
    // best-effort, exactly like the raw setItem it replaces.
    writeClientState(cartPolicy, { items });
    cart.items = items;
  }

  /** Re-read the cart from localStorage into the reactive store. */
  function reloadCart(): void {
    cart.items = readFromStorage().items;
  }

  /** Empty the cart (e.g. after a confirmed/paid order). */
  function clearCart(): void {
    removeClientState(cartPolicy);
    cart.items = [];
  }

  return { cart, saveCart, reloadCart, clearCart };
}
